import {
    useCallback,
    useContext,
    useMemo,
    useRef,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import useWebSocket from './use-websocket';

import AuthContext from '@/contexts/auth';
import { getTokens } from '@/lib/utils';

interface WsOrderMessage {
    event?: string;
    kind?: string;
    action?: string;
    actor_id?: number;
    order?: {
        id?: number;
        uuid?: string;
        status?: string;
        customer_id?: number;
        supplier_id?: number;
        is_modified_by_supplier?: boolean;
        subtotal?: string;
        discount_total?: string;
        grand_total?: string;
        updated_at?: string;
    };
}

function buildWsUrl(): string | null {
    const { accessToken } = getTokens();
    if (!accessToken) return null;

    const serverUrl = import.meta.env
        .VITE_APP_SERVER_URL as string;
    if (!serverUrl) return null;

    const wsBase = serverUrl
        .replace(/^https:\/\//, 'wss://')
        .replace(/^http:\/\//, 'ws://')
        .replace(/\/api\/?$/, '');

    return `${wsBase}/ws/notifications/?token=${accessToken}`;
}

export default function useOrderNotifications() {
    const queryClient = useQueryClient();
    const { state } = useContext(AuthContext);

    const handledRef = useRef<Map<string, number>>(
        new Map(),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute when auth changes
    const wsUrl = useMemo(() => buildWsUrl(), [state]);

    const handleMessage = useCallback(
        (raw: unknown) => {
            const msg = raw as WsOrderMessage;

            if (msg?.event !== 'order_updated' || !msg.order) return;

            const { action, order } = msg;
            if (!action || !order.uuid) return;

            // Deduplicate
            const dedupeKey = `order_updated:${order.uuid}:${action}`;
            const now = Date.now();
            const last = handledRef.current.get(dedupeKey);
            if (last && now - last < 5000) return;
            handledRef.current.set(dedupeKey, now);

            // Clean old entries
            Array.from(handledRef.current.entries())
                .filter(([, ts]) => now - ts > 10000)
                .forEach(([key]) => {
                    handledRef.current.delete(key);
                });

            // Don't show toast for own actions
            if (state?.id && String(msg.actor_id) === String(state.id)) return;

            // Show toast based on action
            switch (action) {
                case 'created':
                    toast.info('New order received');
                    break;
                case 'supplier_accepted':
                    toast.success('Your order was accepted');
                    break;
                case 'supplier_declined':
                    toast.error('Your order was declined');
                    break;
                case 'supplier_modified':
                    toast.info('Your order was modified by the supplier');
                    break;
                case 'customer_accepted':
                    toast.success('Customer accepted the modified order');
                    break;
                case 'customer_rejected':
                    toast.info('Customer rejected the modified order');
                    break;
                default:
                    break;
            }

            // Refresh order lists, detail, and navbar notifications
            queryClient.invalidateQueries({
                queryKey: ['orders'],
            });
            queryClient.invalidateQueries({
                queryKey: ['order-detail', order.uuid],
            });
            queryClient.invalidateQueries({
                queryKey: ['notifications'],
            });
            queryClient.invalidateQueries({
                queryKey: ['notifications-unread-count'],
            });
        },
        [queryClient, state?.id],
    );

    return useWebSocket({
        url: wsUrl || '',
        onMessage: handleMessage,
        enabled: !!wsUrl,
    });
}
